import React, { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import type { RegisterSession, Order, InventoryItem, Recipe } from '../../types';
import RegisterModal from './RegisterModal';
import { canManageRegister } from '../../lib/authUtils';
import { filterOrdersForRegisterClose } from '../../lib/registerCloseOrders';
import {
  computeSessionRegisterSales,
  registerCloseOrdersSnapshotKey,
} from '../../lib/sessionRegisterSales';

const EndOfDayChecklist = lazy(() => import('./EndOfDayChecklist'));

interface RegisterControlProps {
  session: RegisterSession | undefined;
  todaysOrders: Order[];
  sessionOrders?: Order[];
  inventoryItems?: InventoryItem[];
  recipes?: Recipe[];
  onOpenRegister: (openingFloat: number) => void;
  onCloseRegister: (
    closingFloat: number,
    session: RegisterSession,
    todaysSales: { cash: number; card: number; cashDenominations?: { denom: number; count: number }[] },
  ) => void;
  onStockTake?: (physicalCounts: { [itemId: number]: number }) => void;
  onForceCloseSession?: (session: RegisterSession) => void;
  onRequireManagerPin: (action: 'open' | 'close', onApproved: () => void) => void;
  currentUser?: any;
  showForceClose?: boolean;
  onUpdateOrder?: (order: Order) => void | Promise<void>;
  onMarkAsPaid?: (orderId: string) => void | Promise<void>;
}

const RegisterControl: React.FC<RegisterControlProps> = ({
  session,
  todaysOrders,
  sessionOrders = [],
  inventoryItems = [],
  recipes = [],
  onOpenRegister,
  onCloseRegister,
  onStockTake,
  onForceCloseSession,
  onRequireManagerPin,
  currentUser,
  showForceClose = false,
  onUpdateOrder,
  onMarkAsPaid,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);
  const [checklistReady, setChecklistReady] = useState(false);
  const [cashModalReady, setCashModalReady] = useState(false);

  const registerCloseSourceOrders = useMemo(
    () => (sessionOrders.length > 0 ? sessionOrders : todaysOrders),
    [sessionOrders, todaysOrders],
  );

  const registerCloseSourceRef = useRef(registerCloseSourceOrders);
  registerCloseSourceRef.current = registerCloseSourceOrders;

  const registerCloseOrdersKey = useMemo(
    () => registerCloseOrdersSnapshotKey(registerCloseSourceOrders),
    [registerCloseSourceOrders],
  );

  const registerCloseOrders = useMemo(
    () => filterOrdersForRegisterClose(registerCloseSourceRef.current),
    [registerCloseOrdersKey],
  );

  const sessionRegisterSales = useMemo(
    () => computeSessionRegisterSales(registerCloseOrders),
    [registerCloseOrdersKey],
  );

  useEffect(() => {
    if (!isChecklistOpen) {
      setChecklistReady(false);
      return;
    }
    const id = requestAnimationFrame(() => setChecklistReady(true));
    return () => cancelAnimationFrame(id);
  }, [isChecklistOpen]);

  useEffect(() => {
    if (!isModalOpen) {
      setCashModalReady(false);
      return;
    }
    const id = requestAnimationFrame(() => setCashModalReady(true));
    return () => cancelAnimationFrame(id);
  }, [isModalOpen]);

  const isOpen = !!session;
  const buttonText = isOpen ? 'Close Register' : 'Open Register';
  const canManage = canManageRegister(currentUser);

  if (!canManage) {
    return null;
  }

  const requestCloseRegister = () => {
    onRequireManagerPin('close', () => {
      setIsChecklistOpen(true);
    });
  };

  const requestOpenRegister = () => {
    onRequireManagerPin('open', () => {
      setIsModalOpen(true);
    });
  };

  const handleConfirmClose = () => {
    setIsChecklistOpen(false);
    setIsModalOpen(true);
  };

  const emptySessionSales = useMemo(() => ({ cash: 0, card: 0 }), []);

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          data-register-button
          onClick={() => {
            if (session) {
              requestCloseRegister();
            } else {
              requestOpenRegister();
            }
          }}
          className={`text-xs font-bold px-2 py-1 rounded-md transition-colors ${
            isOpen
              ? 'bg-green-500 text-white hover:bg-green-600'
              : 'bg-gray-400 text-white hover:bg-gray-500'
          }`}
        >
          {buttonText}
        </button>

        {session && onForceCloseSession && showForceClose && (
          <button
            type="button"
            onClick={() => {
              if (
                window.confirm(
                  'Force close this register session? This should only be used if the normal close flow failed.',
                )
              ) {
                onForceCloseSession(session);
              }
            }}
            className="text-xs font-bold px-2 py-1 rounded-md bg-red-100 text-red-600 hover:bg-red-200"
          >
            Force Close
          </button>
        )}
      </div>

      {isModalOpen && !cashModalReady && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="rounded-lg bg-white px-6 py-4 shadow-lg text-sm text-gray-600">
            Opening close screen…
          </div>
        </div>
      )}
      {isModalOpen && cashModalReady && (
        <RegisterModal
          session={session}
          sessionSales={session ? sessionRegisterSales : emptySessionSales}
          closedByDisplayName={
            (currentUser?.name && String(currentUser.name).trim()) ||
            (currentUser?.email && String(currentUser.email).trim()) ||
            undefined
          }
          onClose={() => setIsModalOpen(false)}
          onOpenRegister={onOpenRegister}
          onCloseRegister={onCloseRegister}
        />
      )}

      {isChecklistOpen && !checklistReady && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="rounded-lg bg-white px-6 py-4 shadow-lg text-sm text-gray-600">Loading checklist…</div>
        </div>
      )}
      {isChecklistOpen && checklistReady && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="rounded-lg bg-white px-6 py-4 shadow-lg text-sm text-gray-600">
                Loading checklist…
              </div>
            </div>
          }
        >
          <EndOfDayChecklist
            orders={registerCloseOrders}
            sessionOrders={registerCloseOrders}
            registerSession={session}
            inventoryItems={inventoryItems}
            recipes={recipes}
            onClose={() => setIsChecklistOpen(false)}
            onConfirmClose={handleConfirmClose}
            onStockTake={onStockTake}
            onUpdateOrder={onUpdateOrder}
            onMarkAsPaid={onMarkAsPaid}
          />
        </Suspense>
      )}
    </>
  );
};

export default RegisterControl;
